import { faRobot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import VoxelAdvancedTextSearch from '../../../../../components/voxel/Search/VoxelAdvancedTextSearch';
import VoxelAlert from '../../../../../components/voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionButton from '../../../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogActionSubmit from '../../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelCheckbox from '../../../../../components/voxel/Forms/VoxelCheckbox';
import VoxelTextarea from '../../../../../components/voxel/Forms/VoxelTextArea';
import VoxelTable from '../../../../../components/voxel/Tables/VoxelTable';
import VoxelTableCell from '../../../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableHeadCell from '../../../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTablePagination from '../../../../../components/voxel/Tables/VoxelTablePagination';
import {
  default as VoxelTableHead,
  default as VoxelTableRow,
} from '../../../../../components/voxel/Tables/VoxelTableRow';
import VoxelTooltipLong from '../../../../../components/voxel/Tooltip/VoxelTooltipLong';
import { ContentTaskFilteringGq } from '../../../../../graphql/projects/models/ContentTaskFilteringGq';
import { ContentTaskSortingGq } from '../../../../../graphql/projects/models/ContentTaskSortingGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { useForm } from './useForm';

export interface ApplyChatGptDialogProps {
  open: boolean;
  handleClose: () => void;
  filteringState: ContentTaskFilteringGq;
  sortingState: ContentTaskSortingGq;
  project: ProjectGq;
  reload: () => Promise<unknown>;
}

export const ApplyChatGptDialog: React.FC<ApplyChatGptDialogProps> = ({
  open,
  handleClose,
  filteringState,
  sortingState,
  project,
  reload,
}) => {
  // Filtering, paging etc.
  const [page, setPage] = useState(0);
  const [step, setStep] = useState(0);

  const [localFilteringState, setLocalFilteringState] =
    useState<ContentTaskFilteringGq>(filteringState);
  const contentTasksQuery = projectsApiService.queries.useGetContentTasks(
    {
      filtering: localFilteringState,
      rowsPerPage: 8,
      page: page,
      sorting: sortingState,
    },
    { skip: !open }
  );

  const formikProps = useForm(project, localFilteringState, reload, setPage, setStep, handleClose);

  // Reset form upon open - just so that the latest prompt can be included
  useEffect(() => formikProps.resetForm(), [open]);

  // Step 1
  const selection = formikProps.values.selection;
  const taskCount = selection.invertSelection
    ? (contentTasksQuery?.count ?? 0) - selection.taskIds.length
    : selection.taskIds.length;

  let stepTitle: string;
  let nextButtonTitle: string;
  let nextButtonFn: () => void;
  let nextButtonDisabled: boolean = false;

  let prevButtonTitle: undefined | string;
  let prevButtonFn: undefined | (() => void);
  let prevButtonDisabled = formikProps.isSubmitting;

  if (step == 0) {
    stepTitle = 'Step 1: Select pages to draft up content';

    nextButtonTitle = `Continue with ${taskCount} URLs`;
    nextButtonFn = () => setStep(1);
    nextButtonDisabled = !taskCount;
  }
  if (step === 1) {
    stepTitle = 'Step 2: Give ChatGPT appropriate prompt instructions to fit your case';

    nextButtonTitle = `Submit ${taskCount} URLs`;
    nextButtonFn = () => formikProps.submitForm();

    prevButtonTitle = 'Back';
    prevButtonFn = () => setStep((prev) => prev - 1);
  }

  // Back button
  if (step > 0) {
    nextButtonDisabled = !taskCount || formikProps.isSubmitting;
  }

  if (!open) return null;
  return (
    <VoxelDialog
      open={open}
      onClose={() => {
        setStep(0);
        setPage(0);
        formikProps.resetForm();
        handleClose();
      }}
      maxWClass={'max-w-6xl'}
      closeOnBackdrop={false}
      title={stepTitle}
      titleIcon={<FontAwesomeIcon icon={faRobot} size="xl" className="text-primary-600" />}
    >
      <VoxelDialogContent className='mt-6'>
        {step === 0 && (
          <div>
            <VoxelAdvancedTextSearch
              size="sm"
              placeholder="Search url"
              widthClass="w-52"
              value={localFilteringState.urlSearchTerm}
              onChange={(event) =>
                setLocalFilteringState((prev) => ({ ...prev, urlSearchTerm: event.target.value }))
              }
              deleteValue={() => setLocalFilteringState((prev) => ({ ...prev, urlSearchTerm: '' }))}
            />
            <VoxelTable>
              <thead>
                <VoxelTableHead>
                  <VoxelTableCell />
                  <VoxelTableHeadCell>Url</VoxelTableHeadCell>
                  <VoxelTableHeadCell>Primary keyword</VoxelTableHeadCell>
                  <VoxelTableHeadCell>Secondary keyword</VoxelTableHeadCell>
                  <VoxelTableHeadCell>ChatGPT status</VoxelTableHeadCell>
                </VoxelTableHead>
              </thead>
              <tbody>
                {contentTasksQuery.contentTasks?.map((ct) => {
                  const orderedKeywords = orderBy(ct.keywords, (kw) => kw.order);
                  const inTaskIds = selection.taskIds.includes(ct.id);
                  const selected =
                    (inTaskIds && !selection.invertSelection) ||
                    (!inTaskIds && selection.invertSelection);

                  return (
                    <VoxelTableRow
                      key={ct.id}
                      button
                      selected={selected}
                      onClick={() => {
                        const next = { ...selection };
                        if (inTaskIds) {
                          next.taskIds = next.taskIds.filter((id) => id !== ct.id);
                        } else {
                          next.taskIds = [...next.taskIds, ct.id];
                        }
                        formikProps.setFieldValue('selection', next);
                      }}
                    >
                      <VoxelTableCell size="md">
                        <VoxelCheckbox color="primary" checked={selected} />
                      </VoxelTableCell>
                      <VoxelTableCell>{ct.url}</VoxelTableCell>
                      <VoxelTableCell>{orderedKeywords[0]?.keyword}</VoxelTableCell>
                      <VoxelTableCell>{orderedKeywords[1]?.keyword}</VoxelTableCell>
                      <VoxelTableCell>
                        <VoxelTooltipLong
                          disabled={!ct.chatGptResult?.length}
                          message={
                            <pre className="text-[10px] w-[600px] whitespace-pre-wrap p-2">
                              {ct.chatGptResult ?? ''}
                            </pre>
                          }
                        >
                          <span>{ct.chatGptStatus ?? '-'}</span>
                        </VoxelTooltipLong>
                      </VoxelTableCell>
                    </VoxelTableRow>
                  );
                })}
                <VoxelTablePagination
                  colSpan={5}
                  totalCount={contentTasksQuery?.count}
                  currentPage={contentTasksQuery?.page}
                  rowsPerPage={contentTasksQuery?.rowsPerPage}
                  onPageChange={(event, page) => setPage(page)}
                />
              </tbody>
            </VoxelTable>

          </div>
        )}
        {step === 1 && (
          <div>
            <div className={`flex flex-col gap-4 pt-4`}>
              <VoxelTextarea
                label="System prompt"
                value={formikProps.values.system}
                onChange={(event) => formikProps.setFieldValue('system', event.target.value)}
                minRows={12}
              />
              <VoxelTextarea
                label="Content prompt"
                value={formikProps.values.content}
                onChange={(event) => formikProps.setFieldValue('content', event.target.value)}
                minRows={4}
              />
            </div>
          </div>
        )}
      </VoxelDialogContent>
      <VoxelDialogActions>
        {!!prevButtonTitle?.length && (
          <VoxelDialogActionButton
            title={prevButtonTitle}
            onClick={prevButtonFn}
            disabled={prevButtonDisabled}
          />
        )}
        <VoxelDialogActionSubmit
          title={nextButtonTitle}
          onClick={nextButtonFn}
          disabled={nextButtonDisabled}
        />
      </VoxelDialogActions>
      <VoxelAlert severity="warning" size="sm" iconSize="lg" iconPosition='center'>
        Use sparingly, as the feature is computationally intensive. ChatGPT may take 2 minutes
        per URL to complete. Start small and experiment until you get satisfactory results.
      </VoxelAlert>
    </VoxelDialog>
  );
};

export default ApplyChatGptDialog;
